import memoize from 'lru-memoize';
import MarkdownIt from 'markdown-it';

/*
 * Types.
 */

export enum TokenNestingsEnum {
  OPENING = 1,
  SELF_CLOSING = 0,
  CLOSING = -1,
}

/*
 * MarkdownIt configuration.
 */

interface MarkdownItConfiguration {
  preset: 'default' | 'zero';
  enable: ReadonlyArray<string>;
  disable: ReadonlyArray<string>;
  options: MarkdownIt.Options;
}

const markdownConfiguration: MarkdownItConfiguration = {
  preset: 'default',
  enable: [],
  disable: [
    'table', // GFM style tables.
    'hr', // ***, --- and ___ produce a <hr> tag.
    'code', // 4 space padded code blocks.
    'reference', // Reference links.
    'heading', // # headings.
    'lheading', // Headings with === underneath.
  ],
  options: {
    html: true,
    breaks: true,
    linkify: true,
  },
};

const linkifyConfiguration: MarkdownItConfiguration = {
  preset: 'zero',
  enable: [
    'newline', // Required for the "breaks" option.
    'linkify', // Required for the "linkify" option.
  ],
  disable: [],
  options: {
    breaks: true,
    linkify: true,
  },
};

/*
 * Public API.
 */

export function buildMarkdownTokens(text: string) {
  const markdownIt = getMarkdownIt(markdownConfiguration);
  return markdownIt.parse(text, {});
}

export function buildInlineMarkdownTokens(text: string) {
  const markdownIt = getMarkdownIt(markdownConfiguration);
  return markdownIt.parseInline(text, {});
}

export function buildLinkifyTokens(text: string) {
  const markdownIt = getMarkdownIt(linkifyConfiguration);
  return markdownIt.parse(text, {});
}

// Example: https://github.com/remarkjs/remark/blob/main/packages/remark-stringify/lib/macro/block.js#L9
const commonMarkGapRegExp = /^\s*<!-{4,}>\s*$/;

/** Test whether the string is an empty HTML comment used to delimit adjacent lists. */
export function isCommonMarkGap(text: string): boolean {
  return commonMarkGapRegExp.test(text);
}

/*
 * Cache markdown-it instances.
 */

const getMarkdownIt = memoize()(({preset, options, enable, disable}: MarkdownItConfiguration) => {
  // Create the MarkdownIt object.
  const markdownIt = new MarkdownIt(preset, options);

  // Enable/disable some rules.
  markdownIt.disable(disable as Array<string>);
  markdownIt.enable(enable as Array<string>);

  return markdownIt;
});
